import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft, Upload, Download, Search, Sparkles, Music, Users, History,
  X, Filter, Plus, Wand2, FileVideo, ScanFace, Settings, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle, Trash2, Image as ImageIcon, Tag, Lightbulb, Save,
  Film, ScanLine, Palette, Play, Loader2, Cloud, FolderOpen, RefreshCw, ExternalLink,
  AlertTriangle
} from "lucide-react";
import {
  getProject, listChapters, listCuts, listMaterials, listBgm, listPlayers,
  uploadMaterial, fetchCategories, updateCut, assignMaterialToCut, unassignMaterial,
  searchStock, importStockMaterial, createSnapshot, listVersions, restoreVersion,
  aiSuggestMaterials, aiGenerateCaption, aiGenerateNarration, aiCompositionAdvice,
  aiSearchKeywords, exportFcpxmlUrl, exportStorylineJsonUrl, deleteMaterial,
  uploadBgm, syncCutsToBeats, deleteBgm, updateChapter, addCut, deleteCut,
  formatTC, formatDuration, resolveMediaUrl, resolveMaterialUrl, updateMaterial,
  fetchIntroTemplates, buildPlayerIntros, uploadPlayerIntroVideo,
  createCustomTemplateFromImage, listCustomTemplates, deleteCustomTemplate,
  analyzeMaterial, analyzeAllMaterials, getAnalysisStatus,
  getAnalysisCostPreview, autoAssignMaterials,
  splitCut, mergeNextCut, driveAdoptExistingRoot, downloadPremiereGuide,
  driveScanNew, driveImportAllNew,
  getTemplateSuggestions, adoptTemplateIntent, deleteTemplateIntent,
  startRenderMp4, getRenderJob, MEDIA_BASE,
  driveStatus, driveConnect, driveDisconnect, driveListFolders, driveListFiles,
  driveSetupProjectStructure, driveImportFolder, driveSyncPlayersFolder,
  resolveProxyVideoUrl, getMaterialProxyStatus, regenerateMaterialProxy,
  generateMissingProxies, getProjectProxyStatus,
} from "@/lib/api";
import api from "@/lib/api";

// =============================================================================
// Helpers
// =============================================================================
const cutDuration = (c) => Math.max(0, (c.end_at || 0) - (c.start_at || 0));

const buildChapterMap = (chapters, cuts) => {
  const map = {};
  chapters.forEach(ch => { map[ch.id] = { ...ch, cuts: [] }; });
  cuts.forEach(c => { if (map[c.chapter_id]) map[c.chapter_id].cuts.push(c); });
  Object.values(map).forEach(ch => ch.cuts.sort((a, b) => a.start_at - b.start_at));
  return Object.values(map).sort((a, b) => (a.order || 0) - (b.order || 0));
};

// =============================================================================
// Material Library (left panel)
// =============================================================================
const MaterialLibrary = ({ projectId, onPickMaterial, refreshKey, onRefresh, selectedCut }) => {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(0);
  const [showStock, setShowStock] = useState(false);
  const [autoAnalyze, setAutoAnalyze] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [cutFilterMode, setCutFilterMode] = useState(false);
  const [proxyStatus, setProxyStatus] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [m, c] = await Promise.all([listMaterials(projectId, filter), fetchCategories()]);
      setMaterials(m);
      setCategories(c);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, filter, refreshKey]);

  // プロキシ状態を10秒ごとにポーリング（処理中があるときだけ高頻度に）
  useEffect(() => {
    let timer = null;
    const tick = async () => {
      try {
        const s = await getProjectProxyStatus(projectId);
        setProxyStatus(s);
        // 進行中があれば素材リストもrefreshして proxy_status を反映
        if (s.processing > 0 || s.pending > 0 || s.queue_size > 0) {
          load();
        }
      } catch (e) { /* ignore */ }
    };
    tick();
    timer = setInterval(tick, 8000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [projectId, refreshKey]);

  const handleGenerateMissingProxies = async () => {
    try {
      const r = await generateMissingProxies(projectId);
      if (r.queued > 0) {
        alert(`${r.queued}件の素材プロキシ生成をキューに投入しました。バックグラウンドで処理されます。`);
      } else {
        alert("生成対象の素材はありません（すべて完了済み or 進行中）");
      }
    } catch (e) {
      alert("プロキシ生成キューへの投入に失敗しました");
    }
  };

  // 選択カット変更時：候補モードに自動切替＋AI候補取得
  useEffect(() => {
    if (!selectedCut) {
      setCutFilterMode(false);
      setSuggestions([]);
      return;
    }
    let aborted = false;
    setCutFilterMode(true);
    setSuggestLoading(true);
    aiSuggestMaterials(selectedCut.id)
      .then((data) => {
        if (aborted) return;
        const list = Array.isArray(data) ? data : (data?.suggestions || []);
        setSuggestions(list);
      })
      .catch((e) => { console.error(e); if (!aborted) setSuggestions([]); })
      .finally(() => { if (!aborted) setSuggestLoading(false); });
    return () => { aborted = true; };
  }, [selectedCut?.id]);

  // 表示する素材リスト：カット選択時は候補のみスコア順、それ以外は全素材
  const displayMaterials = useMemo(() => {
    if (cutFilterMode && suggestions.length > 0) {
      // suggestions: [{score, material}]
      return suggestions.map(s => ({ ...s.material, _score: s.score }));
    }
    return materials;
  }, [cutFilterMode, suggestions, materials]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    for (const f of Array.from(files)) {
      setUploading(p => p + 1);
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", filter !== "all" ? filter : "個人");
        fd.append("auto_analyze", String(autoAnalyze));
        const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";
        await fetch(`${apiBase}/projects/${projectId}/materials/upload`, { method: "POST", body: fd });
      } catch (e) { console.error(e); }
      setUploading(p => p - 1);
    }
    await load();
    onRefresh && onRefresh();
  };

  return (
    <div className="h-full flex flex-col" data-testid="material-library">
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{borderColor: "var(--border-subtle)"}}>
        <div className="flex items-center gap-2">
          <FileVideo size={14} style={{color: "var(--gold)"}}/>
          <h3 className="text-xs uppercase tracking-widest" style={{color: "var(--text-secondary)"}}>素材ライブラリ</h3>
          <span className="chip text-tc" data-testid="material-count">{displayMaterials.length}</span>
        </div>
        <div className="flex gap-1">
          <button
            className="btn-ghost !p-1.5"
            onClick={() => fileInputRef.current?.click()}
            title="アップロード"
            data-testid="upload-material-button"
          >
            <Upload size={12}/>
          </button>
          <button
            className="btn-ghost !p-1.5"
            onClick={() => setShowStock(s => !s)}
            title="ストック検索"
            data-testid="stock-search-toggle"
          >
            <Search size={12}/>
          </button>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          accept="video/*,image/*,.heic,.heif"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="material-file-input"
        />
      </div>

      <div className="px-4 py-2 border-b flex items-center justify-between gap-2" style={{borderColor: "var(--border-subtle)"}}>
        <label className="flex items-center gap-2 text-[10px] cursor-pointer" style={{color: "var(--text-secondary)"}} title="アップロード時にAIで顔・背番号・シーンを自動解析">
          <input type="checkbox" checked={autoAnalyze} onChange={e => setAutoAnalyze(e.target.checked)} data-testid="auto-analyze-toggle"/>
          自動AI解析
        </label>
        <span className="text-[9px]" style={{color: "var(--text-muted)"}}>HEIC対応</span>
      </div>

      {/* プロキシ生成ステータス（軽量MP4） */}
      {proxyStatus && (proxyStatus.ready + proxyStatus.processing + proxyStatus.pending + proxyStatus.failed + proxyStatus.none > 0) && (
        <div className="px-4 py-2 border-b flex items-center justify-between gap-2 text-[10px]"
             style={{borderColor: "var(--border-subtle)", background: "rgba(212,165,116,0.04)"}}
             data-testid="proxy-status-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <Film size={10} style={{color: "var(--gold)"}}/>
            <span style={{color: "var(--text-secondary)"}}>軽量プロキシ:</span>
            <span style={{color: "#88B27A"}} data-testid="proxy-ready-count">完了 {proxyStatus.ready}</span>
            {(proxyStatus.processing > 0 || proxyStatus.queue_size > 0) && (
              <span style={{color: "var(--gold)"}} data-testid="proxy-processing-count">
                <Loader2 size={9} className="inline animate-spin mr-0.5"/>
                処理中 {proxyStatus.processing + proxyStatus.queue_size}
              </span>
            )}
            {proxyStatus.failed > 0 && (
              <span style={{color: "#C66A4A"}} data-testid="proxy-failed-count">失敗 {proxyStatus.failed}</span>
            )}
            {proxyStatus.none > 0 && (
              <span style={{color: "var(--text-muted)"}}>未生成 {proxyStatus.none}</span>
            )}
          </div>
          {(proxyStatus.none > 0 || proxyStatus.failed > 0) && (
            <button
              className="btn-ghost !text-[10px] !px-2 !py-0.5"
              onClick={handleGenerateMissingProxies}
              title="未生成・失敗の素材を一括でプロキシ生成キューに投入"
              data-testid="generate-missing-proxies-btn"
            >
              <Wand2 size={9}/> 一括生成
            </button>
          )}
        </div>
      )}

      <div className="px-4 py-2 border-b" style={{borderColor: "var(--border-subtle)"}}>
        <div className="flex gap-1 overflow-x-auto scrollbar-hidden">
          <button
            className={`chip whitespace-nowrap ${filter === "all" ? "chip-gold" : ""}`}
            onClick={() => setFilter("all")}
            data-testid="filter-all"
          >すべて</button>
          {categories.map(c => (
            <button
              key={c}
              className={`chip whitespace-nowrap ${filter === c ? "chip-gold" : ""}`}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c}`}
            >{c}</button>
          ))}
        </div>
      </div>

      {showStock && (
        <StockSearchPanel projectId={projectId} onImported={() => { load(); onRefresh && onRefresh(); }}/>
      )}

      {/* カット選択時：候補モードバッジ */}
      {cutFilterMode && selectedCut && (
        <div className="px-4 py-2 border-b flex items-center justify-between gap-2" style={{borderColor: "var(--border-subtle)", background: "rgba(212,165,116,0.06)"}}>
          <div className="text-[10px] flex-1 min-w-0">
            <div className="flex items-center gap-1.5" style={{color: "var(--gold)"}}>
              <Sparkles size={10}/>
              <span className="font-medium">Cut {selectedCut.cut_no} の候補表示中</span>
              {suggestLoading && <Loader2 size={10} className="animate-spin"/>}
            </div>
            <div className="truncate mt-0.5" style={{color: "var(--text-muted)"}} title={selectedCut.title}>
              {selectedCut.title}
            </div>
          </div>
          <button
            className="btn-ghost !text-[10px] !px-2 !py-1"
            onClick={() => setCutFilterMode(false)}
            data-testid="clear-cut-filter"
          >全素材</button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {uploading > 0 && (
          <div className="panel p-3 flex items-center gap-2 text-xs shimmer-gold">
            <Upload size={12}/> {uploading} 件アップロード中...
          </div>
        )}
        {displayMaterials.length === 0 && uploading === 0 && (
          <div className="text-center py-12 text-xs" style={{color: "var(--text-muted)"}}>
            {cutFilterMode ? (
              <>このカットに合う候補がありません<br/><span className="text-[10px]">「全素材」を押して手動で割当</span></>
            ) : (
              <>ファイルをドラッグ、または上の<br/>アップロードボタンから追加</>
            )}
          </div>
        )}
        {displayMaterials.map(m => (
          <MaterialCard key={m.id} material={m} score={m._score} onPick={onPickMaterial} onDeleted={() => { load(); onRefresh && onRefresh(); }}/>
        ))}
      </div>
    </div>
  );
};

const MaterialCard = ({ material, onPick, onDeleted, score }) => {
  const [busy, setBusy] = useState(false);
  const onDel = async (e) => {
    e.stopPropagation();
    if (!window.confirm("この素材を削除しますか？")) return;
    setBusy(true);
    try { await deleteMaterial(material.id); onDeleted && onDeleted(); } finally { setBusy(false); }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="panel p-2 cursor-pointer hover:border-[var(--gold)] transition-colors group"
      onClick={() => onPick && onPick(material)}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("material/id", material.id); }}
      data-testid={`material-card-${material.id}`}
    >
      <div className="aspect-video rounded overflow-hidden mb-2 relative" style={{background: "var(--bg-deep)"}}>
        {material.thumbnail_url ? (
          <img src={resolveMediaUrl(material.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
        ) : material.drive_file_id && material.is_video ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/drive/thumbnail/${material.drive_file_id}`} alt="" className="w-full h-full object-cover" loading="lazy"/>
        ) : material.drive_file_id && material.is_image ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/drive/proxy/${material.drive_file_id}`} alt="" className="w-full h-full object-cover" loading="lazy"/>
        ) : material.is_image ? (
          <ImageIcon size={24} style={{color: "var(--text-muted)"}} className="m-auto mt-6"/>
        ) : (
          <FileVideo size={24} style={{color: "var(--text-muted)"}} className="m-auto mt-6"/>
        )}
        {material.duration > 0 && (
          <span className="absolute bottom-1 right-1 text-[10px] text-tc px-1.5 py-0.5 rounded" style={{background: "rgba(10,13,18,0.9)", color: "var(--gold)"}}>
            {formatDuration(material.duration)}
          </span>
        )}
        {typeof score === "number" && (
          <span className="absolute top-1 left-1 text-[10px] text-tc px-1.5 py-0.5 rounded font-semibold" style={{background: "var(--gold)", color: "#0A0D12"}}>
            {score}%
          </span>
        )}
        {material.is_video && material.drive_file_id && (
          <span
            className="absolute bottom-1 left-1 text-[9px] px-1 py-0.5 rounded font-semibold"
            style={{
              background: material.proxy_status === "ready" ? "rgba(136,178,122,0.85)" :
                          material.proxy_status === "processing" ? "rgba(212,165,116,0.85)" :
                          material.proxy_status === "failed" ? "rgba(198,106,74,0.85)" :
                          "rgba(120,120,120,0.7)",
              color: "#0A0D12"
            }}
            title={`プロキシ: ${material.proxy_status || "未生成"}`}
            data-testid={`proxy-badge-${material.id}`}
          >
            {material.proxy_status === "ready" ? "Proxy" :
             material.proxy_status === "processing" ? "..." :
             material.proxy_status === "pending" ? "wait" :
             material.proxy_status === "failed" ? "ERR" : "—"}
          </span>
        )}
        <button
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-[var(--bg-deep)] rounded p-1"
          onClick={onDel}
          disabled={busy}
          data-testid={`delete-material-${material.id}`}
        >
          <Trash2 size={10} style={{color: "var(--red-warn)"}}/>
        </button>
      </div>
      <div className="text-xs truncate font-medium" style={{color: "var(--text-primary)"}}>{material.original_name}</div>
      {material.ai_best_use_case && (
        <div
          className="mt-1 text-[10px] leading-tight px-1.5 py-1 rounded flex items-start gap-1"
          style={{background: "rgba(212,165,116,0.10)", border: "1px solid rgba(212,165,116,0.25)", color: "var(--gold)"}}
          title={`AIお薦め: ${material.ai_best_use_case}\n${material.scene_description || ""}`}
          data-testid={`ai-best-use-${material.id}`}
        >
          <Sparkles size={9} className="mt-px shrink-0"/>
          <span className="line-clamp-2">{material.ai_best_use_case}</span>
        </div>
      )}
      <div className="flex justify-between items-center mt-1 gap-1 flex-wrap">
        <span className="chip text-[9px]">{material.category}</span>
        {material.ai_emotion && material.ai_emotion !== "不明" && (
          <span className="chip text-[9px]" style={{color: "var(--text-secondary)"}}>{material.ai_emotion}</span>
        )}
        {material.source !== "uploaded" && <span className="chip text-[9px]" style={{color: "var(--blue-deep)"}}>{material.source}</span>}
      </div>
    </motion.div>
  );
};

const StockSearchPanel = ({ projectId, onImported }) => {
  const [q, setQ] = useState("emotional sports");
  const [source, setSource] = useState("pexels");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const search = async () => {
    setLoading(true);
    try {
      const data = await searchStock(q, source);
      setResults(data.results || []);
    } finally { setLoading(false); }
  };
  const importMat = async (r) => {
    await importStockMaterial(projectId, { ...r, category: "風景" });
    onImported && onImported();
  };
  return (
    <div className="border-b p-3 space-y-2" style={{borderColor: "var(--border-subtle)", background: "var(--bg-deep)"}} data-testid="stock-search-panel">
      <div className="text-[10px] uppercase tracking-widest" style={{color: "var(--text-muted)"}}>ストック動画検索（無料・商用OK）</div>
      <div className="flex gap-1">
        <input className="cinema text-xs" placeholder="emotional sports" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && search()} data-testid="stock-search-input"/>
        <select className="cinema text-xs !w-auto" value={source} onChange={e => setSource(e.target.value)}>
          <option value="pexels">Pexels</option>
          <option value="pixabay">Pixabay</option>
        </select>
        <button className="btn-ghost !px-2" onClick={search} disabled={loading} data-testid="stock-search-go"><Search size={12}/></button>
      </div>
      {results.length === 0 && !loading && (
        <p className="text-[10px]" style={{color: "var(--text-muted)"}}>
          ※ APIキー未設定の場合は結果が出ません。/app/backend/.env に PEXELS_API_KEY または PIXABAY_API_KEY を追加してください。
        </p>
      )}
      <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
        {results.map(r => (
          <div key={r.id} className="panel p-1 cursor-pointer hover:border-[var(--gold)]" onClick={() => importMat(r)} data-testid={`stock-result-${r.id}`}>
            {r.thumbnail && <img src={r.thumbnail} alt="" className="aspect-video object-cover rounded"/>}
            <div className="text-[10px] mt-1 truncate">{r.user}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// Center Panel: Preview + AI suggestions
// =============================================================================
const CenterPanel = ({ project, selectedCut, materials, onAssigned, refreshKey }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewMaterial, setPreviewMaterial] = useState(null);

  useEffect(() => {
    setSuggestions([]);
    if (!selectedCut) return;
    const assigned = materials.find(m => m.id === selectedCut.assigned_material_id);
    setPreviewMaterial(assigned || null);
  }, [selectedCut, materials]);

  const fetchSuggestions = async () => {
    if (!selectedCut) return;
    setLoading(true);
    try {
      const data = await aiSuggestMaterials(selectedCut.id);
      setSuggestions(data.suggestions || []);
    } finally { setLoading(false); }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    if (!selectedCut) return;
    const materialId = e.dataTransfer.getData("material/id");
    if (!materialId) return;
    await assignMaterialToCut(selectedCut.id, materialId, 0);
    onAssigned && onAssigned();
  };

  if (!selectedCut) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="empty-preview">
        <div className="text-center max-w-sm">
          <FileVideo size={48} style={{color: "var(--gold)"}} className="mx-auto mb-6 opacity-50"/>
          <p className="font-display text-lg mb-2">右の絵コンテからカットを選択</p>
          <p className="text-xs" style={{color: "var(--text-muted)"}}>選択するとプレビューと素材候補が表示されます。</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-full flex flex-col"
      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dropzone-active"); }}
      onDragLeave={(e) => e.currentTarget.classList.remove("dropzone-active")}
      onDrop={(e) => { e.currentTarget.classList.remove("dropzone-active"); onDrop(e); }}
      data-testid="center-panel"
    >
      {/* Cut header */}
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{borderColor: "var(--border-subtle)"}}>
        <div>
          <div className="flex items-center gap-3">
            <span className="text-tc text-xs" style={{color: "var(--gold)"}}>Cut {selectedCut.cut_no || "?"}</span>
            <span className="text-tc text-xs" style={{color: "var(--text-muted)"}}>
              {formatTC(selectedCut.start_at)} → {formatTC(selectedCut.end_at)}
            </span>
            <span className="chip text-[10px]">{selectedCut.category}</span>
          </div>
          <h3 className="font-display text-xl mt-1" data-testid="selected-cut-title">{selectedCut.title}</h3>
          <p className="text-xs mt-1" style={{color: "var(--text-secondary)"}}>{selectedCut.description}</p>
        </div>
        <div className="flex gap-2">
          {selectedCut.assigned_material_id && (
            <button
              className="btn-ghost"
              onClick={async () => { await unassignMaterial(selectedCut.id); onAssigned(); }}
              data-testid="unassign-button"
            ><X size={12}/> 割当解除</button>
          )}
        </div>
      </div>

      {/* Preview area */}
      <div className="flex-1 p-6 flex items-center justify-center" style={{background: "var(--bg-deep)"}}>
        <div className="aspect-video w-full max-w-3xl rounded panel-elevated overflow-hidden flex items-center justify-center" style={{background: "#000"}} data-testid="preview-area">
          {previewMaterial ? (
            previewMaterial.is_video ? (
              previewMaterial.proxy_status === "ready" ? (
                // 軽量MP4プロキシ（最優先・即時再生・Cookie不要）
                <video
                  src={resolveProxyVideoUrl(previewMaterial)}
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain"
                  key={`proxy-${previewMaterial.id}`}
                  data-testid="preview-video"
                />
              ) : previewMaterial.drive_file_id ? (
                // プロキシ未完成: Drive iframe にフォールバック（生成中/失敗時）
                <div className="relative w-full h-full">
                  <iframe
                    src={`https://drive.google.com/file/d/${previewMaterial.drive_file_id}/preview`}
                    className="w-full h-full"
                    allow="autoplay"
                    allowFullScreen
                    key={previewMaterial.id}
                    data-testid="preview-video"
                    title={previewMaterial.original_name || "preview"}
                  />
                  <div className="absolute top-2 right-2 px-2 py-1 rounded text-[10px]"
                       style={{background: "rgba(10,13,18,0.85)", color: "var(--gold)"}}
                       data-testid="proxy-status-badge">
                    {previewMaterial.proxy_status === "processing" && "プロキシ生成中..."}
                    {previewMaterial.proxy_status === "pending" && "プロキシ待機中"}
                    {previewMaterial.proxy_status === "failed" && "プロキシ失敗 - Drive再生中"}
                    {!previewMaterial.proxy_status && "Drive直接再生"}
                  </div>
                </div>
              ) : (
                <video
                  src={resolveMaterialUrl(previewMaterial)}
                  controls
                  className="w-full h-full object-contain"
                  key={previewMaterial.id}
                  data-testid="preview-video"
                />
              )
            ) : (
              <img src={resolveMaterialUrl(previewMaterial)} alt="" className="w-full h-full object-contain"/>
            )
          ) : (
            <div className="text-center" style={{color: "var(--text-muted)"}}>
              <ImageIcon size={32} className="mx-auto mb-3 opacity-40"/>
              <p className="text-xs">素材を割り当てるとプレビューが表示されます</p>
              <p className="text-[10px] mt-2">左から動画をドラッグ、または下のAI候補から選択</p>
            </div>
          )}
        </div>
      </div>

      {/* AI suggestions / cut details */}
      <div className="border-t" style={{borderColor: "var(--border-subtle)"}}>
        <div className="px-6 py-3 flex items-center justify-between">
          <h4 className="text-xs uppercase tracking-widest" style={{color: "var(--text-secondary)"}}>
            <Sparkles size={11} className="inline mr-1" style={{color: "var(--gold)"}}/> AI 素材候補
          </h4>
          <button className="btn-ghost text-xs" onClick={fetchSuggestions} disabled={loading} data-testid="ai-suggest-button">
            <Wand2 size={11}/> {loading ? "解析中..." : "候補を取得"}
          </button>
        </div>
        <div className="px-6 pb-6">
          {suggestions.length === 0 ? (
            <p className="text-xs" style={{color: "var(--text-muted)"}}>「候補を取得」を押すと、カテゴリ・尺・記述からスコア付けして表示します。</p>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {suggestions.map(({score, material}) => (
                <div
                  key={material.id}
                  className="panel p-2 cursor-pointer hover:border-[var(--gold)]"
                  onClick={async () => { await assignMaterialToCut(selectedCut.id, material.id, 0); onAssigned(); }}
                  data-testid={`suggestion-${material.id}`}
                >
                  <div className="aspect-video rounded overflow-hidden mb-2" style={{background: "var(--bg-deep)"}}>
                    {material.thumbnail_url ? (
                      <img src={resolveMediaUrl(material.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
                    ) : (
                      <FileVideo size={20} style={{color: "var(--text-muted)"}} className="m-auto mt-4"/>
                    )}
                  </div>
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="truncate" style={{color: "var(--text-primary)"}}>{material.original_name}</span>
                    <span className="chip chip-gold !text-[9px] !px-1">{score}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Right Panel: Storyboard with chapters and cuts
// =============================================================================
const StoryboardPanel = ({ chapters, cuts, materials, selectedCutId, onSelectCut, onCutsChanged }) => {
  const grouped = useMemo(() => buildChapterMap(chapters, cuts), [chapters, cuts]);
  const [collapsed, setCollapsed] = useState({});
  const [editingCut, setEditingCut] = useState(null);

  return (
    <div className="h-full flex flex-col" data-testid="storyboard-panel">
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{borderColor: "var(--border-subtle)"}}>
        <div className="flex items-center gap-2">
          <FileVideo size={14} style={{color: "var(--gold)"}}/>
          <h3 className="text-xs uppercase tracking-widest" style={{color: "var(--text-secondary)"}}>絵コンテ</h3>
        </div>
        <span className="text-[10px] text-tc" style={{color: "var(--text-muted)"}}>{cuts.length} カット</span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {grouped.map(ch => (
          <div key={ch.id} data-testid={`chapter-block-${ch.chapter_no}`}>
            <button
              className="w-full flex items-center justify-between py-2 px-2 rounded hover:bg-[var(--bg-elevated)]"
              onClick={() => setCollapsed(c => ({ ...c, [ch.id]: !c[ch.id] }))}
            >
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full" style={{background: ch.color}}/>
                <div className="text-left">
                  <div className="font-display text-base" style={{color: "var(--text-primary)"}}>第{ch.chapter_no}章 {ch.title}</div>
                  <div className="text-[10px]" style={{color: "var(--text-muted)"}}>{ch.subtitle}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-tc text-[10px]" style={{color: "var(--text-muted)"}}>
                  {formatDuration(ch.cuts.reduce((s, c) => s + cutDuration(c), 0))} / {formatDuration(ch.target_duration)}
                </span>
                {collapsed[ch.id] ? <ChevronDown size={12}/> : <ChevronUp size={12}/>}
              </div>
            </button>
            {!collapsed[ch.id] && (
              <div className="pl-2 mt-1 space-y-1.5">
                {ch.is_template_section && (
                  <div className="panel p-2 text-[10px] flex items-start gap-2" style={{borderLeft: "3px dashed var(--gold)", color: "var(--text-secondary)"}}>
                    <Sparkles size={11} style={{color: "var(--gold)"}} className="flex-shrink-0 mt-0.5"/>
                    <span>{ch.template_note} — Final Cut Pro / Premiereで配置します</span>
                  </div>
                )}
                {ch.cuts.map(cut => {
                  const targetDur = cutDuration(cut);
                  const assigned = materials.find(m => m.id === cut.assigned_material_id);
                  const overflow = assigned && assigned.duration > 0 && assigned.duration < targetDur - 0.2;
                  const state = !cut.assigned_material_id ? "unassigned" : overflow ? "overflow" : "assigned";
                  return (
                    <div
                      key={cut.id}
                      className="cut-card text-[11px]"
                      data-state={state}
                      data-selected={cut.id === selectedCutId}
                      onClick={() => onSelectCut(cut)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={async (e) => {
                        e.preventDefault();
                        const materialId = e.dataTransfer.getData("material/id");
                        if (materialId) {
                          await assignMaterialToCut(cut.id, materialId, 0);
                          onCutsChanged();
                        }
                      }}
                      data-testid={`cut-card-${cut.cut_no}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-tc text-[10px]" style={{color: "var(--gold)"}}>Cut {cut.cut_no}</span>
                        <span className="text-tc text-[10px]" style={{color: "var(--text-muted)"}}>
                          {formatDuration(targetDur)}
                          {assigned && assigned.duration > 0 && (
                            <span className={overflow ? "text-[var(--red-warn)] ml-1" : "text-[var(--green-success)] ml-1"}>
                              {overflow ? `(素材${formatDuration(assigned.duration)})` : "✓"}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="font-medium mb-1" style={{color: "var(--text-primary)"}}>{cut.title}</div>
                      <div className="text-[10px] truncate" style={{color: "var(--text-muted)"}}>{cut.description}</div>
                      {cut.caption && (
                        <div className="mt-1 text-[10px] chip chip-gold !py-0">"{cut.caption}"</div>
                      )}
                      {cut.template_intents && cut.template_intents.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {cut.template_intents.slice(0, 3).map(t => (
                            <span key={t.id} className="text-[9px] px-1.5 py-0.5 rounded" style={{background: "rgba(168,192,154,0.18)", color: "var(--green-success)", border: "1px solid rgba(168,192,154,0.5)"}}>
                              📦 {t.type}
                            </span>
                          ))}
                          {cut.template_intents.length > 3 && (
                            <span className="text-[9px]" style={{color: "var(--text-muted)"}}>+{cut.template_intents.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button
                  className="btn-ghost text-[10px] w-full justify-center !py-1.5"
                  onClick={async () => {
                    const last = ch.cuts[ch.cuts.length - 1];
                    const startAt = last ? last.end_at : ch.start_at;
                    await addCut(ch.project_id, {
                      chapter_id: ch.id,
                      start_at: startAt,
                      end_at: startAt + 5,
                      title: "新しいカット",
                      description: "",
                      required_materials: "",
                      category: "個人",
                    });
                    onCutsChanged();
                  }}
                  data-testid={`add-cut-${ch.chapter_no}`}
                ><Plus size={10}/> カットを追加</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// Bottom Timeline (visual)
// =============================================================================
const TimelineBar = ({ chapters, cuts, materials, totalDuration, selectedCutId, onSelectCut, bgmTracks }) => {
  const containerRef = useRef(null);
  return (
    <div className="px-6 py-3 border-t" style={{borderColor: "var(--border-subtle)", background: "var(--bg-deep)"}} data-testid="timeline-bar">
      <div className="flex items-center justify-between mb-2 text-[10px]" style={{color: "var(--text-muted)"}}>
        <span className="uppercase tracking-widest">Timeline</span>
        <span className="text-tc" style={{color: "var(--gold)"}}>00:00 → {formatTC(totalDuration)}</span>
      </div>
      <div className="relative" ref={containerRef}>
        <div className="timeline-track" data-testid="timeline-cuts-track">
          {cuts.sort((a, b) => a.start_at - b.start_at).map(cut => {
            const ch = chapters.find(c => c.id === cut.chapter_id);
            const left = (cut.start_at / totalDuration) * 100;
            const width = (cutDuration(cut) / totalDuration) * 100;
            const assigned = materials.find(m => m.id === cut.assigned_material_id);
            const bg = assigned ? (ch?.color || "#D4A574") : "rgba(226,125,96,0.4)";
            return (
              <div
                key={cut.id}
                className="timeline-cut-block"
                style={{ left: `${left}%`, width: `${width}%`, background: bg, opacity: cut.id === selectedCutId ? 1 : 0.85, outline: cut.id === selectedCutId ? "2px solid var(--text-primary)" : "none" }}
                onClick={() => onSelectCut(cut)}
                title={`Cut ${cut.cut_no}: ${cut.title}`}
                data-testid={`timeline-cut-${cut.cut_no}`}
              >
                {width > 1.2 && cut.cut_no}
              </div>
            );
          })}
        </div>
        {bgmTracks.length > 0 && (
          <div className="timeline-bgm-track" data-testid="timeline-bgm-track">
            {bgmTracks.map(b => {
              const left = (b.start_at / totalDuration) * 100;
              const width = (b.duration / totalDuration) * 100;
              return (
                <div
                  key={b.id}
                  className="absolute h-full flex items-center px-2 text-[9px] truncate"
                  style={{ left: `${left}%`, width: `${width}%`, background: "rgba(43,122,158,0.25)", borderLeft: "2px solid var(--blue-deep)", color: "var(--text-secondary)" }}
                  title={b.name}
                  data-testid={`timeline-bgm-${b.id}`}
                >
                  <Music size={9} className="mr-1"/> {b.name} · {b.bpm}BPM
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Side dialogs
// =============================================================================
const ExportDialog = ({ project, onClose }) => {
  const [renderJobId, setRenderJobId] = useState(null);
  const [renderStatus, setRenderStatus] = useState(null);
  const [uploadToDrive, setUploadToDrive] = useState(true);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveLocalMount, setDriveLocalMount] = useState(() => {
    try { return localStorage.getItem("drive_local_mount") || ""; } catch { return ""; }
  });

  useEffect(() => {
    driveStatus().then(s => setDriveConnected(s.connected));
  }, []);

  useEffect(() => {
    if (!renderJobId) return;
    const iv = setInterval(async () => {
      const s = await getRenderJob(renderJobId);
      setRenderStatus(s);
      if ((s.status === "completed" || s.status === "error") &&
          (!uploadToDrive || s.drive_upload_status === "completed" || (s.drive_upload_status || "").startsWith("error") || s.drive_upload_status === "skipped_not_connected" || s.drive_upload_status === "no_drive_folder")) {
        clearInterval(iv);
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [renderJobId, uploadToDrive]);

  const startRender = async () => {
    const r = await api.post(`/projects/${project.id}/export/render_mp4`, { upload_to_drive: uploadToDrive && driveConnected }).then(x => x.data);
    setRenderJobId(r.job_id);
    setRenderStatus({ status: "queued", progress: 0, message: "キューに追加..." });
  };

  return (
    <DialogShell title="書き出し" onClose={onClose} testId="export-dialog">
      <div className="grid md:grid-cols-4 gap-4">
        <div className="panel p-6">
          <FileVideo size={28} style={{color: "var(--gold)"}} className="mx-auto mb-3"/>
          <h4 className="font-display text-lg mb-2 text-center">FCPXML</h4>
          <p className="text-[11px] mb-3 text-center" style={{color: "var(--text-secondary)"}}>
            Final Cut Pro / Premiere Pro CC 2024 用<br/>章マーカー付き
          </p>
          <div className="mb-3">
            <label className="text-[10px] block mb-1" style={{color: "var(--text-secondary)"}}>
              <Cloud size={10} className="inline mr-1"/> Drive Desktopのローカルパス（任意）
            </label>
            <input
              type="text"
              className="w-full text-[10px] px-2 py-1.5 rounded"
              style={{background: "var(--bg-deep)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)"}}
              placeholder="/Users/.../マイドライブ/2026_3年生送り出し"
              value={driveLocalMount}
              onChange={e => {
                setDriveLocalMount(e.target.value);
                try { localStorage.setItem("drive_local_mount", e.target.value); } catch {}
              }}
              data-testid="drive-local-mount-input"
            />
            <p className="text-[9px] mt-1" style={{color: "var(--text-muted)"}}>
              Macで Google ドライブ for Desktop を同期している場合、ここにプロジェクトルートのパスを入れると、各カットに動画が自動リンクされます。
            </p>
          </div>
          <a className="btn-gold inline-flex !text-xs w-full justify-center" href={exportFcpxmlUrl(project.id, driveLocalMount)} download data-testid="download-fcpxml">
            <Download size={12}/> ダウンロード
          </a>
        </div>

        <div className="panel p-6 text-center" style={{borderColor: "var(--gold)"}}>
          <Film size={28} style={{color: "var(--gold)"}} className="mx-auto mb-3"/>
          <h4 className="font-display text-lg mb-2">MP4 直接レンダリング</h4>
          <p className="text-[11px] mb-3" style={{color: "var(--text-secondary)"}}>
            1080p / 30fps / H.264<br/>テロップ・BGM合成済み
          </p>

          {!renderJobId && (
            <label className={`flex items-center gap-2 mb-3 text-[11px] justify-center ${driveConnected ? "" : "opacity-50"}`}>
              <input type="checkbox" checked={uploadToDrive && driveConnected} onChange={e => setUploadToDrive(e.target.checked)} disabled={!driveConnected} data-testid="upload-to-drive-checkbox"/>
              <Cloud size={11}/>
              {driveConnected ? "完成後 Drive `_OUTPUT` に自動アップ" : "Drive未接続のためアップ不可"}
            </label>
          )}

          {!renderJobId ? (
            <button className="btn-gold inline-flex !text-xs" onClick={startRender} data-testid="start-render-mp4">
              <Play size={12}/> レンダリング開始
            </button>
          ) : renderStatus?.status === "completed" ? (
            <div className="space-y-2">
              <a className="btn-gold inline-flex !text-xs" href={`${MEDIA_BASE}${renderStatus.download_url}`} download data-testid="download-mp4">
                <Download size={12}/> MP4 ({renderStatus.file_size_mb}MB)
              </a>
              {renderStatus.drive_upload_status === "uploading" && (
                <div className="text-[10px] flex items-center justify-center gap-1" style={{color: "var(--blue-deep)"}}>
                  <Loader2 size={10} className="animate-spin"/> Driveにアップ中...
                </div>
              )}
              {renderStatus.drive_upload_status === "completed" && renderStatus.drive_view_url && (
                <a href={renderStatus.drive_view_url} target="_blank" rel="noopener noreferrer" className="text-[10px] flex items-center justify-center gap-1 hover:underline" style={{color: "var(--green-success)"}} data-testid="drive-uploaded-link">
                  <CheckCircle2 size={10}/> Driveにアップ完了 <ExternalLink size={9}/>
                </a>
              )}
              {(renderStatus.drive_upload_status || "").startsWith("error") && (
                <div className="text-[10px]" style={{color: "var(--red-warn)"}}>Driveアップエラー</div>
              )}
            </div>
          ) : (
            <div className="text-xs">
              <Loader2 size={16} className="mx-auto mb-2 animate-spin" style={{color: "var(--gold)"}}/>
              <div className="mb-2" style={{color: "var(--text-secondary)"}}>{renderStatus?.message || "処理中..."}</div>
              <div className="h-1 rounded-full overflow-hidden" style={{background: "var(--bg-deep)"}}>
                <div className="h-full" style={{width: `${renderStatus?.progress || 0}%`, background: "var(--gold)", transition: "width 0.5s"}}/>
              </div>
              <div className="text-tc text-[10px] mt-1">{renderStatus?.progress || 0}%</div>
            </div>
          )}
        </div>

        <div className="panel p-6 text-center">
          <Save size={28} style={{color: "var(--gold)"}} className="mx-auto mb-3"/>
          <h4 className="font-display text-lg mb-2">構成案 JSON</h4>
          <p className="text-[11px] mb-4" style={{color: "var(--text-secondary)"}}>
            ストーリーラインのみ<br/>共有・別プロジェクトに復元可
          </p>
          <a className="btn-gold inline-flex !text-xs" href={exportStorylineJsonUrl(project.id)} download data-testid="download-json">
            <Download size={12}/> ダウンロード
          </a>
        </div>

        <div className="panel p-6 text-center" style={{borderColor: "rgba(168,192,154,0.5)"}}>
          <Sparkles size={28} style={{color: "var(--gold)"}} className="mx-auto mb-3"/>
          <h4 className="font-display text-lg mb-2">Premiere 仕上げ指示書</h4>
          <p className="text-[11px] mb-4" style={{color: "var(--text-secondary)"}}>
            章別カラー・速度・テロップ<br/>BGM配置・Runwayプロンプト集
          </p>
          <button
            className="btn-gold inline-flex !text-xs"
            onClick={async () => {
              try {
                await downloadPremiereGuide(project.id);
              } catch (e) {
                alert("生成失敗: " + (e.response?.data?.detail || e.message));
              }
            }}
            data-testid="download-premiere-guide"
          >
            <Download size={12}/> 指示書を生成
          </button>
        </div>
      </div>
      <div className="mt-6 p-4 panel text-xs" style={{color: "var(--text-secondary)"}}>
        <p className="mb-2"><strong style={{color: "var(--gold)"}}>📌 推奨ワークフロー（Premiere Pro 中心）：</strong></p>
        <ol className="space-y-1 list-decimal pl-5">
          <li><strong>FCPXML</strong> → Premiere Pro に Import → タイムラインに自動展開</li>
          <li><strong>Premiere 仕上げ指示書</strong> をダウンロード → Markdown を開いて章ごとの色・速度・BGM配置を順に適用</li>
          <li>不足シーンは <strong>Runway Gen-4</strong> で生成（指示書内のプロンプト集を使用）</li>
          <li>最終仕上げ → Premiere から書き出し（指示書 5章のレンダリング設定参照）</li>
          <li><strong>MP4直接レンダリング</strong> はラフ確認用（5〜15分で叩き台が見られる）</li>
        </ol>
      </div>
    </DialogShell>
  );
};

const VersionsDialog = ({ projectId, onClose, onRestored }) => {
  const [versions, setVersions] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => setVersions(await listVersions(projectId));
  useEffect(() => { load(); }, [projectId]);

  const snap = async () => {
    setBusy(true);
    try { await createSnapshot(projectId, label); setLabel(""); await load(); } finally { setBusy(false); }
  };
  const restore = async (vid) => {
    if (!window.confirm("このバージョンを復元します。現在の編集状態は上書きされます。")) return;
    await restoreVersion(vid);
    onRestored && onRestored();
    onClose();
  };

  return (
    <DialogShell title="バージョン管理" onClose={onClose} testId="versions-dialog">
      <div className="flex gap-2 mb-6">
        <input className="cinema text-sm" placeholder="例：構成案V2" value={label} onChange={e => setLabel(e.target.value)} data-testid="snapshot-label-input"/>
        <button className="btn-gold" onClick={snap} disabled={busy} data-testid="create-snapshot-button">
          <Save size={12}/> 現在を保存
        </button>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {versions.length === 0 && <p className="text-xs" style={{color: "var(--text-muted)"}}>まだバージョンはありません</p>}
        {versions.map(v => (
          <div key={v.id} className="panel p-3 flex justify-between items-center" data-testid={`version-${v.id}`}>
            <div>
              <div className="text-sm font-medium">{v.label}</div>
              <div className="text-[10px]" style={{color: "var(--text-muted)"}}>{new Date(v.created_at).toLocaleString("ja-JP")}</div>
            </div>
            <button className="btn-ghost" onClick={() => restore(v.id)} data-testid={`restore-${v.id}`}>復元</button>
          </div>
        ))}
      </div>
    </DialogShell>
  );
};

const PlayersDialog = ({ projectId, onClose }) => {
  const [players, setPlayers] = useState([]);
  const [name, setName] = useState("");
  const [num, setNum] = useState("");
  const [pos, setPos] = useState("");

  const load = async () => setPlayers(await listPlayers(projectId));
  useEffect(() => { load(); }, [projectId]);

  const add = async () => {
    if (!name.trim()) return;
    await fetch("", { method: "GET" }).catch(() => {});
    await import("@/lib/api").then(({ createPlayer }) => createPlayer(projectId, { name: name.trim(), jersey_number: num, position: pos }));
    setName(""); setNum(""); setPos("");
    load();
  };
  const onPhoto = async (pid, file) => {
    if (!file) return;
    const { uploadPlayerPhoto } = await import("@/lib/api");
    await uploadPlayerPhoto(pid, file);
    load();
  };
  const onIntroVideo = async (pid, file) => {
    if (!file) return;
    await uploadPlayerIntroVideo(pid, file);
    load();
  };
  const onDel = async (pid) => {
    if (!window.confirm("削除しますか？")) return;
    const { deletePlayer } = await import("@/lib/api");
    await deletePlayer(pid);
    load();
  };

  return (
    <DialogShell title="3年生 選手管理" onClose={onClose} testId="players-dialog">
      <p className="text-xs mb-4" style={{color: "var(--text-secondary)"}}>
        参考写真を登録しておくと、選手紹介セクション（Cut 06-07）で素材を整理しやすくなります。
      </p>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <input className="cinema text-sm" placeholder="名前" value={name} onChange={e => setName(e.target.value)} data-testid="player-name-input"/>
        <input className="cinema text-sm" placeholder="背番号" value={num} onChange={e => setNum(e.target.value)} data-testid="player-jersey-input"/>
        <input className="cinema text-sm" placeholder="ポジション" value={pos} onChange={e => setPos(e.target.value)} data-testid="player-position-input"/>
      </div>
      <button className="btn-gold mb-6" onClick={add} disabled={!name.trim()} data-testid="add-player-button"><Plus size={12}/> 選手を追加</button>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {players.map(p => (
          <div key={p.id} className="panel p-3" data-testid={`player-${p.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-[11px]" style={{color: "var(--text-muted)"}}>
                  {p.jersey_number && `#${p.jersey_number}`} {p.position && ` · ${p.position}`}
                </div>
              </div>
              <button className="btn-danger-ghost !p-1" onClick={() => onDel(p.id)}><Trash2 size={10}/></button>
            </div>
            <div className="flex gap-2 flex-wrap items-end">
              {(p.photo_urls || []).map((u, i) => (
                <img key={i} src={resolveMediaUrl(u)} alt="" className="w-12 h-12 rounded object-cover"/>
              ))}
              <label className="w-12 h-12 rounded border border-dashed flex items-center justify-center cursor-pointer text-[10px]" style={{borderColor: "var(--border-strong)", color: "var(--text-muted)"}} title="顔写真追加">
                <Plus size={14}/>
                <input type="file" className="hidden" accept="image/*" onChange={e => onPhoto(p.id, e.target.files?.[0])}/>
              </label>
              <label className={`px-2 py-1 rounded text-[10px] cursor-pointer flex items-center gap-1 ${p.intro_video_url ? "chip-success" : "chip"}`} style={{borderStyle: "dashed"}} title="紹介動画">
                <Film size={11}/>
                {p.intro_video_url ? "紹介動画 ✓" : "紹介動画追加"}
                <input type="file" className="hidden" accept="video/*" onChange={e => onIntroVideo(p.id, e.target.files?.[0])}/>
              </label>
            </div>
          </div>
        ))}
      </div>
    </DialogShell>
  );
};

const BgmDialog = ({ projectId, totalDuration, onClose, onChanged }) => {
  const [tracks, setTracks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => setTracks(await listBgm(projectId));
  useEffect(() => { load(); }, [projectId]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadBgm(projectId, file, file.name, 0);
      await load();
      onChanged && onChanged();
    } finally { setUploading(false); }
  };

  const sync = async (bgmId) => {
    const data = await syncCutsToBeats(projectId, bgmId);
    alert(`${data.adjusted} カットをビートに同期しました`);
    onChanged && onChanged();
  };

  const onDel = async (id) => {
    if (!window.confirm("削除しますか？")) return;
    await deleteBgm(id);
    load();
    onChanged && onChanged();
  };

  return (
    <DialogShell title="BGM・音楽駆動編集" onClose={onClose} testId="bgm-dialog">
      <p className="text-xs mb-4" style={{color: "var(--text-secondary)"}}>
        BGMをアップロードすると、BPMとピーク（盛り上がり点）を自動検出。<br/>
        「ビートに同期」で、近接するカットの開始時刻を音楽の拍に自動吸着します。
      </p>
      <button className="btn-gold mb-6" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-bgm-button">
        <Upload size={12}/> {uploading ? "解析中..." : "BGMをアップロード"}
      </button>
      <input ref={fileRef} type="file" className="hidden" accept="audio/*" onChange={e => upload(e.target.files?.[0])}/>

      <div className="space-y-2">
        {tracks.length === 0 && <p className="text-xs" style={{color: "var(--text-muted)"}}>まだBGMがありません</p>}
        {tracks.map(t => (
          <div key={t.id} className="panel p-4" data-testid={`bgm-track-${t.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-[11px] text-tc" style={{color: "var(--text-muted)"}}>
                  {formatDuration(t.duration)} · {t.bpm} BPM · ピーク {t.peaks.length}箇所
                </div>
              </div>
              <button className="btn-danger-ghost !p-1" onClick={() => onDel(t.id)}><Trash2 size={10}/></button>
            </div>
            <audio src={resolveMediaUrl(t.file_url)} controls className="w-full mb-3" style={{filter: "invert(0.85)"}}/>
            <button className="btn-gold w-full" onClick={() => sync(t.id)} data-testid={`sync-bgm-${t.id}`}>
              <Wand2 size={12}/> このBGMのビートにカットを同期
            </button>
          </div>
        ))}
      </div>
    </DialogShell>
  );
};

const PlayerIntroBuilderDialog = ({ projectId, onClose, onBuilt }) => {
  const [templates, setTemplates] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("classic_spot");
  const [duration, setDuration] = useState(4.0);
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);

  const customFileRef = useRef(null);
  const [customNotes, setCustomNotes] = useState("");
  const [creatingCustom, setCreatingCustom] = useState(false);

  const load = async () => {
    const [t, c, p] = await Promise.all([fetchIntroTemplates(), listCustomTemplates(), listPlayers(projectId)]);
    setTemplates(t); setCustoms(c); setPlayers(p);
  };
  useEffect(() => { load(); }, [projectId]);

  const build = async () => {
    if (!players.length) {
      alert("先に選手を登録してください");
      return;
    }
    setBuilding(true);
    setResult(null);
    try {
      const r = await buildPlayerIntros(projectId, { template_id: selectedTemplate, duration_per_player: duration });
      setResult(r);
      onBuilt && onBuilt();
    } catch (e) {
      alert("生成に失敗しました：" + (e.response?.data?.detail || e.message));
    } finally {
      setBuilding(false);
    }
  };

  const onCustomUpload = async (file) => {
    if (!file) return;
    setCreatingCustom(true);
    try {
      const r = await createCustomTemplateFromImage(file, customNotes);
      if (r.id) {
        await load();
        alert(`カスタムテンプレ「${r.spec?.name_suggestion || "無題"}」を作成しました。`);
      } else {
        alert("テンプレ作成に失敗：" + (r.error || "unknown"));
      }
    } finally {
      setCreatingCustom(false);
      setCustomNotes("");
    }
  };

  const allTemplates = [...templates, ...customs.map(c => ({ id: c.id, name: c.name + " (カスタム)", description: c.spec?.overall_mood || "Motion Array再現", duration_per_player: 4.0, preview_color: "#88B27A", custom: true }))];

  return (
    <DialogShell title="選手紹介ビルダー" onClose={onClose} testId="player-intro-dialog">
      <p className="text-xs mb-4" style={{color: "var(--text-secondary)"}}>
        登録済み選手 <strong style={{color: "var(--gold)"}}>{players.length}名</strong> 分の紹介クリップを自動生成し、第2章（選手紹介）に自動配置します。
      </p>

      <div className="mb-6">
        <label className="text-[10px] uppercase tracking-widest mb-2 block" style={{color: "var(--text-muted)"}}>テンプレート選択</label>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 max-h-72 overflow-y-auto">
          {allTemplates.map(t => (
            <button
              key={t.id}
              className={`panel p-4 text-left transition-all ${selectedTemplate === t.id ? "border-[var(--gold)] bg-[rgba(212,165,116,0.06)]" : ""}`}
              onClick={() => setSelectedTemplate(t.id)}
              data-testid={`select-template-${t.id}`}
            >
              <div className="w-full h-20 rounded mb-2 flex items-center justify-center" style={{background: `linear-gradient(135deg, ${t.preview_color}33, ${t.preview_color}66)`}}>
                <Palette size={20} style={{color: t.preview_color}}/>
              </div>
              <div className="font-medium text-sm mb-1" style={{color: selectedTemplate === t.id ? "var(--gold)" : "var(--text-primary)"}}>{t.name}</div>
              <div className="text-[10px] leading-relaxed" style={{color: "var(--text-muted)"}}>{t.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-[10px] uppercase tracking-widest mb-2 block" style={{color: "var(--text-muted)"}}>1選手あたりの尺（秒）</label>
          <input type="number" step="0.5" min="2" max="8" className="cinema text-sm text-tc" value={duration} onChange={e => setDuration(parseFloat(e.target.value))} data-testid="duration-input"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest mb-2 block" style={{color: "var(--text-muted)"}}>合計尺（{players.length}名）</label>
          <div className="cinema text-sm text-tc !cursor-default flex items-center" style={{color: "var(--gold)"}}>
            {formatDuration(players.length * duration)}
          </div>
        </div>
      </div>

      <button
        className="btn-gold w-full mb-6"
        onClick={build}
        disabled={building || !players.length}
        data-testid="build-intros-button"
      >
        <Wand2 size={14}/> {building ? "生成中..." : `${players.length}名の紹介クリップを自動生成`}
      </button>

      {result && (
        <div className="panel p-4 mb-6 text-xs" style={{borderColor: "var(--green-success)"}}>
          <CheckCircle2 size={14} className="inline mr-2" style={{color: "var(--green-success)"}}/>
          {result.generated} 名分のクリップを生成し、第2章に配置しました。素材ライブラリにも追加されています。
        </div>
      )}

      {/* Custom template creation */}
      <div className="border-t pt-6" style={{borderColor: "var(--border-subtle)"}}>
        <h4 className="font-display text-lg mb-2">Motion Arrayデザインを再現</h4>
        <p className="text-xs mb-4" style={{color: "var(--text-secondary)"}}>
          気に入ったテンプレ動画/画像をアップロード → AIがデザインを分析してカスタムテンプレを作成
        </p>
        <textarea
          className="cinema text-xs mb-3"
          rows={2}
          placeholder="補足メモ（例：もっと暖色系で、文字大きめにしたい）"
          value={customNotes}
          onChange={e => setCustomNotes(e.target.value)}
          data-testid="custom-notes-input"
        />
        <button
          className="btn-ghost w-full"
          onClick={() => customFileRef.current?.click()}
          disabled={creatingCustom}
          data-testid="upload-custom-template-button"
        >
          <Upload size={12}/> {creatingCustom ? "AIで分析中..." : "テンプレ画像/動画をアップロード"}
        </button>
        <input ref={customFileRef} type="file" className="hidden" accept="image/*,video/*" onChange={e => onCustomUpload(e.target.files?.[0])}/>
      </div>
    </DialogShell>
  );
};

const AnalysisDialog = ({ projectId, onClose }) => {
  const [status, setStatus] = useState({ counts: {}, total: 0 });
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  const load = async () => {
    setStatus(await getAnalysisStatus(projectId));
    try { setCost(await getAnalysisCostPreview(projectId, true)); } catch (e) {}
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [projectId]);

  const runAll = async (only_pending) => {
    // コスト確認
    try {
      const c = await getAnalysisCostPreview(projectId, only_pending);
      const target = only_pending ? "未解析" : "全";
      const ok = window.confirm(
        `🤖 高精度AI解析を実行します（${target}件）\n\n` +
        `対象：動画 ${c.video_count}本 / 画像 ${c.image_count}枚\n` +
        `推定コスト：約 ¥${c.estimated_cost_yen}\n` +
        `推定時間：約 ${c.estimated_minutes} 分\n\n` +
        `内容：キーフレーム7枚 + 音声書き起こし(Whisper) + GPT-5.2 Vision統合解析\n` +
        `各素材から「シーン記述・選手・背番号・感情・推奨章・最適カット」を抽出します。\n\n` +
        `実行してよろしいですか？`
      );
      if (!ok) return;
    } catch (e) {}

    setBusy(true);
    try {
      const r = await analyzeAllMaterials(projectId, only_pending);
      alert(`${r.queued} 件をバックグラウンド解析中。\n進捗はこの画面のカウンターで確認できます。`);
      load();
    } finally {
      setBusy(false);
    }
  };

  const runAutoAssign = async (overwrite) => {
    const ok = window.confirm(
      overwrite
        ? "⚠️ 全カットの素材配置を上書きします。よろしいですか？"
        : "✨ AI解析結果に基づき、空きカットに最適素材を自動配置します。\n（既に配置されているカットはそのままです）"
    );
    if (!ok) return;
    setAssigning(true);
    setAssignResult(null);
    try {
      const r = await autoAssignMaterials(projectId, overwrite);
      setAssignResult(r);
    } catch (e) {
      alert("自動配置に失敗しました: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAssigning(false);
    }
  };

  const completed = status.counts?.completed || 0;
  const canAutoAssign = completed > 0;

  return (
    <DialogShell title="AI 解析（高精度モード）" onClose={onClose} testId="analysis-dialog">
      <p className="text-xs mb-4" style={{color: "var(--text-secondary)"}}>
        各素材から <b style={{color:"var(--gold)"}}>キーフレーム7枚 + 音声書き起こし + Vision統合解析</b> で
        シーン記述・選手・背番号・感情・推奨章・最適カット候補を抽出します。<br/>
        Drive素材も自動で一時DL→解析→即削除（サーバー容量0運用）。
      </p>

      {/* コストプレビュー */}
      {cost && cost.total_count > 0 && (
        <div className="panel p-3 mb-4 text-[11px]" data-testid="cost-preview"
             style={{background:"rgba(212,165,116,0.08)", borderColor:"rgba(212,165,116,0.3)"}}>
          <div className="flex justify-between items-center">
            <div>
              <span style={{color:"var(--text-muted)"}}>未解析対象：</span>
              <span style={{color:"var(--text-primary)"}}> 動画 {cost.video_count}本 / 画像 {cost.image_count}枚</span>
            </div>
            <div>
              <span style={{color:"var(--text-muted)"}}>推定コスト：</span>
              <b style={{color:"var(--gold)"}}>¥{cost.estimated_cost_yen}</b>
              <span style={{color:"var(--text-muted)"}} className="ml-2">/ 約{cost.estimated_minutes}分</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-5 gap-2 mb-4">
        {[
          { k: "not_analyzed", l: "未解析", c: "var(--text-muted)" },
          { k: "pending", l: "キュー", c: "var(--text-secondary)" },
          { k: "running", l: "解析中", c: "var(--gold)" },
          { k: "completed", l: "完了", c: "var(--green-success)" },
          { k: "error", l: "エラー", c: "var(--red-warn)" },
        ].map(({ k, l, c }) => (
          <div key={k} className="panel p-3 text-center" data-testid={`analysis-count-${k}`}>
            <div className="text-2xl font-display text-tc" style={{color: c}}>{status.counts?.[k] || 0}</div>
            <div className="text-[10px]" style={{color: "var(--text-muted)"}}>{l}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mb-4">
        <button className="btn-gold flex-1" onClick={() => runAll(true)} disabled={busy} data-testid="analyze-pending-button">
          <ScanLine size={14}/> {busy ? "実行中..." : "未解析を高精度解析"}
        </button>
        <button className="btn-ghost flex-1" onClick={() => runAll(false)} disabled={busy} data-testid="analyze-all-button">
          <ScanLine size={14}/> 全件再解析
        </button>
      </div>

      {/* AI自動配置セクション */}
      <div className="panel p-3 mt-4" style={{background:"rgba(212,165,116,0.05)", borderColor:"rgba(212,165,116,0.25)"}}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} style={{color:"var(--gold)"}}/>
          <b className="text-xs" style={{color:"var(--text-primary)"}}>AIで全カットへ自動配置</b>
          <span className="text-[10px]" style={{color:"var(--text-muted)"}}>解析済み: {completed}件</span>
        </div>
        <p className="text-[11px] mb-3" style={{color: "var(--text-secondary)"}}>
          解析結果（章・選手・カテゴリ・シーン・感情・尺・品質）をスコアリングし、各カットに最適素材を自動セット。
          編集の<b style={{color:"var(--gold)"}}>9割</b>はこれで完了します。
        </p>
        <div className="flex gap-2">
          <button className="btn-gold flex-1" onClick={() => runAutoAssign(false)}
                  disabled={!canAutoAssign || assigning} data-testid="auto-assign-button">
            <Wand2 size={14}/> {assigning ? "配置中..." : "空きカットに自動配置"}
          </button>
          <button className="btn-ghost flex-1" onClick={() => runAutoAssign(true)}
                  disabled={!canAutoAssign || assigning} data-testid="auto-assign-overwrite-button">
            <Wand2 size={14}/> 全上書き
          </button>
        </div>
        {!canAutoAssign && (
          <p className="text-[10px] mt-2" style={{color:"var(--text-muted)"}}>
            ※ まずAI解析を完了させてください
          </p>
        )}
        {assignResult && (
          <div className="mt-3 text-[11px]" data-testid="auto-assign-result" style={{color:"var(--text-secondary)"}}>
            <b style={{color:"var(--green-success)"}}>✓ {assignResult.assigned}カット</b> に自動配置
            （スキップ {assignResult.skipped} / 全{assignResult.total_cuts}カット）
            {assignResult.assigned > 0 && (
              <details className="mt-2 cursor-pointer">
                <summary style={{color:"var(--gold)"}}>配置ログを見る</summary>
                <ul className="mt-2 space-y-1 max-h-40 overflow-auto">
                  {assignResult.details?.map((d, i) => (
                    <li key={i} className="text-[10px]">
                      <span style={{color:"var(--gold)"}}>[{d.score}]</span> {d.cut_title} ← {d.material_name}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 p-3 panel text-[11px]" style={{color: "var(--text-secondary)"}}>
        💡 アップロード時の「自動解析」をONにすると、追加した素材が随時バックグラウンドで解析されます。
      </div>
    </DialogShell>
  );
};

const AdvisorDialog = ({ projectId, onClose }) => {
  const [advice, setAdvice] = useState("");
  const [loading, setLoading] = useState(false);
  const fetchAdvice = async () => {
    setLoading(true);
    try { const data = await aiCompositionAdvice(projectId); setAdvice(data.advice); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAdvice(); /* eslint-disable-next-line */ }, []);
  return (
    <DialogShell title="AI 構成アドバイザー" onClose={onClose} testId="advisor-dialog">
      <p className="text-xs mb-4" style={{color: "var(--text-secondary)"}}>
        現在の構成をAIが分析して、改善ポイントを3つ提案します。
      </p>
      {loading ? (
        <div className="panel p-6 text-center text-xs shimmer-gold">分析中...</div>
      ) : (
        <div className="panel p-6 text-sm whitespace-pre-line leading-relaxed" data-testid="advisor-result">{advice || "まだ分析結果がありません"}</div>
      )}
      <button className="btn-ghost mt-4" onClick={fetchAdvice} disabled={loading}><Wand2 size={12}/> 再分析</button>
    </DialogShell>
  );
};

const DriveDialog = ({ projectId, project, onClose, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [folders, setFolders] = useState([]);
  const [path, setPath] = useState([{ id: "root", name: "マイドライブ" }]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [setupResult, setSetupResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [projectName, setProjectName] = useState(project?.name || "Farewell_Video_2027");

  const refreshStatus = async () => setStatus(await driveStatus());
  const loadFolders = async (parent_id = "root") => {
    setBusy(true);
    try {
      const r = await driveListFolders(parent_id);
      setFolders(r.folders || []);
    } catch (e) {
      alert("Driveアクセスエラー：" + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  useEffect(() => {
    if (status?.connected) {
      loadFolders(path[path.length - 1].id);
    }
  }, [status, path]);

  const onConnect = async () => {
    const { authorization_url } = await driveConnect();
    const win = window.open(authorization_url, "_blank", "width=600,height=700");
    // ポーリングで接続状態を確認
    const iv = setInterval(async () => {
      const s = await driveStatus();
      if (s.connected) {
        setStatus(s);
        clearInterval(iv);
        try { win?.close(); } catch (_) {}
      }
    }, 2500);
    setTimeout(() => clearInterval(iv), 120000);
  };

  const onDisconnect = async () => {
    if (!window.confirm("Drive 連携を解除しますか？")) return;
    await driveDisconnect();
    setStatus({ connected: false });
    setFolders([]);
  };

  const enterFolder = (folder) => {
    setPath(p => [...p, { id: folder.id, name: folder.name }]);
    setSelectedFolder(null);
  };
  const goUp = (idx) => {
    setPath(p => p.slice(0, idx + 1));
    setSelectedFolder(null);
  };

  const setupStructure = async () => {
    if (!window.confirm(`プロジェクトフォルダ「${projectName}」をマイドライブの直下に作成します。\n（中に必要なサブフォルダも全て自動作成されます）\n\n進めて良いですか？`)) return;
    setBusy(true);
    try {
      const r = await driveSetupProjectStructure({
        project_name: projectName,
        project_id: projectId,
      });
      setSetupResult(r);
      // ステータスをリロード
      loadFolders("root");
      onChanged && onChanged();
    } catch (e) {
      alert("作成エラー：" + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const importFolder = async () => {
    if (!selectedFolder) return;
    if (!window.confirm(`「${selectedFolder.name}」配下のすべての動画/画像をプロジェクトの素材として取り込みます。\nDriveに保存されたまま、参照として登録されます。\n\n進めますか？`)) return;
    setBusy(true);
    try {
      const r = await driveImportFolder(projectId, selectedFolder.id, true, false);
      setImportResult(r);
      onChanged && onChanged();
    } catch (e) {
      alert("インポートエラー：" + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const syncPlayers = async () => {
    if (!selectedFolder) {
      alert("先に `_players` フォルダを選択してください");
      return;
    }
    if (!window.confirm(`「${selectedFolder.name}」配下のサブフォルダから3年生選手を自動登録します。\nフォルダ名は「12_山田太郎_QB」のような形式を想定。\n\n進めますか？`)) return;
    setBusy(true);
    try {
      const r = await driveSyncPlayersFolder(projectId, selectedFolder.id);
      alert(`${r.created}名の選手を登録しました（${r.total_drive_folders}個のフォルダから）`);
      onChanged && onChanged();
    } catch (e) {
      alert("同期エラー：" + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <DialogShell title="Google Drive 連携" onClose={onClose} testId="drive-dialog">
      {/* 接続状態 */}
      <div className="panel p-4 mb-6 flex items-center justify-between" data-testid="drive-status-panel">
        <div className="flex items-center gap-3">
          <Cloud size={20} style={{color: status?.connected ? "var(--green-success)" : "var(--text-muted)"}}/>
          <div>
            {status?.connected ? (
              <>
                <div className="text-sm font-medium" style={{color: "var(--green-success)"}}>✓ 接続中</div>
                <div className="text-[11px]" style={{color: "var(--text-muted)"}}>{status.email}</div>
              </>
            ) : (
              <>
                <div className="text-sm" style={{color: "var(--text-secondary)"}}>未接続</div>
                <div className="text-[11px]" style={{color: "var(--text-muted)"}}>Googleアカウントでログインしてください</div>
              </>
            )}
          </div>
        </div>
        {status?.connected ? (
          <button className="btn-danger-ghost" onClick={onDisconnect} data-testid="drive-disconnect">解除</button>
        ) : (
          <button className="btn-gold" onClick={onConnect} data-testid="drive-connect">
            <Cloud size={12}/> Driveに接続
          </button>
        )}
      </div>

      {!status?.connected && (
        <div className="panel p-4 text-xs" style={{color: "var(--text-secondary)"}}>
          <p className="mb-2"><strong style={{color: "var(--gold)"}}>📌 接続後にできること：</strong></p>
          <ul className="space-y-1 list-disc pl-5">
            <li>プロジェクトフォルダ構造をワンクリック自動作成</li>
            <li>Driveの動画を素材ライブラリに一括インポート（参照のみ・容量0）</li>
            <li>`_players/` フォルダから選手を自動登録</li>
            <li>完成MP4をDriveの `_OUTPUT` に自動保存</li>
          </ul>
        </div>
      )}

      {status?.connected && (
        <>
          {/* プロジェクト構造作成 */}
          <div className="panel p-4 mb-4">
            <h4 className="font-display text-base mb-2">📁 プロジェクトフォルダ構造を自動作成</h4>
            <p className="text-[11px] mb-3" style={{color: "var(--text-secondary)"}}>
              `_templates`, `_players`, `_bgm`, `01_春`, `02_夏`, `03_秋`, `04_支える人`, `05_風景_装備`, `_OUTPUT` を一括作成します。
            </p>
            <div className="flex gap-2">
              <input className="cinema text-sm" value={projectName} onChange={e => setProjectName(e.target.value)} data-testid="drive-project-name"/>
              <button className="btn-gold whitespace-nowrap" onClick={setupStructure} disabled={busy} data-testid="drive-setup-button">
                <Wand2 size={12}/> 自動作成
              </button>
            </div>
            {setupResult && (
              <div className="mt-3 p-3 panel text-xs" style={{borderColor: "var(--green-success)"}}>
                <CheckCircle2 size={12} className="inline mr-2" style={{color: "var(--green-success)"}}/>
                {Object.keys(setupResult.folder_map).length}個のフォルダを作成しました。<br/>
                ルート: <code className="text-tc">{setupResult.project_name}</code>
              </div>
            )}
          </div>

          {/* 既存Drive構造を採用 */}
          <div className="panel p-4 mb-4" style={{borderColor: "rgba(168,192,154,0.4)"}}>
            <h4 className="font-display text-base mb-2">🔗 既にあるDriveフォルダ構造を採用</h4>
            <p className="text-[11px] mb-3" style={{color: "var(--text-secondary)"}}>
              既に同じ構造（`_players`, `01_春` 等）のフォルダがある場合、ここでそのフォルダを「選択」してこのプロジェクトに紐付けます。<br/>
              <strong className="text-[var(--gold)]">本番デプロイ時の再セットアップに最適。投稿APIが使えるようになります。</strong>
            </p>
            <button
              className="btn-ghost w-full"
              onClick={async () => {
                if (!selectedFolder) { alert("採用するルートフォルダを上で選択してください"); return; }
                if (!window.confirm(`「${selectedFolder.name}」を本プロジェクトのDriveルートとして採用します。よろしいですか？`)) return;
                setBusy(true);
                try {
                  const r = await driveAdoptExistingRoot(projectId, selectedFolder.id, selectedFolder.name, true);
                  alert(`採用成功: フォルダ ${r.folder_map_count} 個 / 選手 ${r.players_synced?.created || 0} 名 を取り込み`);
                  onChanged && onChanged();
                } catch (e) {
                  alert("採用失敗: " + (e.response?.data?.detail || e.message));
                } finally { setBusy(false); }
              }}
              disabled={!selectedFolder || busy}
              data-testid="drive-adopt-button"
            >
              <CheckCircle2 size={12}/> 選択中のフォルダを「このプロジェクトのDriveルート」として採用
            </button>
          </div>

          {/* フォルダブラウザ */}
          <div className="panel p-4 mb-4">
            <h4 className="font-display text-base mb-2">🗂 Drive 内をブラウズして取り込み</h4>
            <div className="flex items-center gap-1 mb-2 flex-wrap text-xs">
              {path.map((p, i) => (
                <React.Fragment key={p.id}>
                  <button onClick={() => goUp(i)} className="text-[var(--gold)] hover:underline" data-testid={`drive-breadcrumb-${i}`}>{p.name}</button>
                  {i < path.length - 1 && <span style={{color: "var(--text-muted)"}}>/</span>}
                </React.Fragment>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto mb-3" style={{minHeight: "8rem"}}>
              {busy && folders.length === 0 && <div className="col-span-3 text-center text-xs py-4 shimmer-gold">読み込み中...</div>}
              {!busy && folders.length === 0 && <div className="col-span-3 text-center text-xs py-4" style={{color: "var(--text-muted)"}}>フォルダがありません</div>}
              {folders.map(f => (
                <button
                  key={f.id}
                  className={`panel p-3 text-left text-xs transition-colors hover:border-[var(--gold)] ${selectedFolder?.id === f.id ? "border-[var(--gold)] bg-[rgba(212,165,116,0.06)]" : ""}`}
                  onClick={() => setSelectedFolder(selectedFolder?.id === f.id ? null : f)}
                  onDoubleClick={() => enterFolder(f)}
                  data-testid={`drive-folder-${f.id}`}
                >
                  <FolderOpen size={14} className="mb-1" style={{color: "var(--gold)"}}/>
                  <div className="truncate">{f.name}</div>
                </button>
              ))}
            </div>
            <p className="text-[10px] mb-3" style={{color: "var(--text-muted)"}}>
              📌 クリックで選択、ダブルクリックで中に入る
            </p>
            <div className="flex gap-2">
              <button className="btn-gold flex-1" onClick={importFolder} disabled={!selectedFolder || busy} data-testid="drive-import-folder">
                <Download size={12}/> このフォルダの動画を素材として取込
              </button>
              <button className="btn-ghost" onClick={syncPlayers} disabled={!selectedFolder || busy} data-testid="drive-sync-players" title="`_players` フォルダ用">
                <Users size={12}/> 選手同期
              </button>
            </div>
            {importResult && (
              <div className="mt-3 p-3 panel text-xs" style={{borderColor: "var(--green-success)"}}>
                <CheckCircle2 size={12} className="inline mr-2" style={{color: "var(--green-success)"}}/>
                <strong>{importResult.imported}件</strong>取り込み完了 / スキップ {importResult.skipped} / 全 {importResult.total_files}
              </div>
            )}
          </div>

          <div className="text-[10px]" style={{color: "var(--text-muted)"}}>
            💡 `_templates`, `_OUTPUT`, `_bgm` 配下は自動的にスキップされます
          </div>
        </>
      )}
    </DialogShell>
  );
};

const DialogShell = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background: "rgba(6,8,16,0.85)", backdropFilter: "blur(8px)"}} onClick={onClose}>
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="panel-elevated p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-3xl">{title}</h2>
        <button className="btn-ghost !p-2" onClick={onClose} data-testid={`close-${testId}`}><X size={14}/></button>
      </div>
      {children}
    </motion.div>
  </div>
);

// =============================================================================
// Cut Inspector (right side detail panel for selected cut)
// =============================================================================
const CutInspector = ({ cut, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [splitParts, setSplitParts] = useState(2);
  if (!cut) return null;
  const onAiCaption = async () => {
    setBusy(true);
    try { await aiGenerateCaption(cut.id); onUpdated(); } finally { setBusy(false); }
  };
  const onChange = async (field, value) => {
    await updateCut(cut.id, { [field]: value });
    onUpdated();
  };
  const onSplit = async () => {
    if (!window.confirm(`このカットを ${splitParts} 個に分割しますか？`)) return;
    setBusy(true);
    try {
      await splitCut(cut.id, splitParts);
      onUpdated();
    } catch (e) {
      alert("分割失敗: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const onMergeNext = async () => {
    if (!window.confirm("このカットを次のカットと結合しますか？")) return;
    setBusy(true);
    try {
      await mergeNextCut(cut.id);
      onUpdated();
    } catch (e) {
      alert("結合失敗: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const dur = (parseFloat(cut.end_at || 0) - parseFloat(cut.start_at || 0)).toFixed(1);
  return (
    <div className="px-6 py-4 border-t space-y-3 text-xs" style={{borderColor: "var(--border-subtle)", background: "var(--bg-elevated)"}} data-testid="cut-inspector">
      <div className="flex items-center justify-between">
        <h4 className="text-xs uppercase tracking-widest" style={{color: "var(--text-secondary)"}}>カット詳細</h4>
        <button className="btn-ghost !text-[10px]" onClick={onAiCaption} disabled={busy} data-testid="generate-caption-button">
          <Wand2 size={10}/> {busy ? "生成中..." : "AIテロップ案"}
        </button>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider mb-1" style={{color: "var(--text-muted)"}}>テロップ</label>
        <input className="cinema text-xs" value={cut.caption || ""} onChange={e => onChange("caption", e.target.value)} placeholder="ここに表示するテロップ" data-testid="caption-input"/>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider mb-1" style={{color: "var(--text-muted)"}}>開始</label>
          <input type="number" step="0.1" className="cinema text-xs text-tc" value={cut.start_at} onChange={e => onChange("start_at", parseFloat(e.target.value))} data-testid="start-input"/>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider mb-1" style={{color: "var(--text-muted)"}}>終了</label>
          <input type="number" step="0.1" className="cinema text-xs text-tc" value={cut.end_at} onChange={e => onChange("end_at", parseFloat(e.target.value))} data-testid="end-input"/>
        </div>
      </div>

      {/* カット分割／結合 */}
      <div className="border-t pt-3" style={{borderColor: "var(--border-subtle)"}}>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-wider" style={{color: "var(--text-muted)"}}>カット分割（{dur}秒 → 数カットに分ける）</label>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="cinema text-xs !py-1 !w-auto"
            value={splitParts}
            onChange={e => setSplitParts(parseInt(e.target.value))}
            data-testid="split-parts-select"
          >
            {[2,3,4,5,6].map(n => <option key={n} value={n}>{n}個に分割</option>)}
          </select>
          <button
            className="btn-ghost !text-[10px] !py-1.5 !px-3"
            onClick={onSplit}
            disabled={busy}
            data-testid="split-cut-button"
            title="このカット枠を等分し、複数素材を当て込めるようにします"
          >
            <Sparkles size={10}/> 分割実行
          </button>
          <button
            className="btn-ghost !text-[10px] !py-1.5 !px-3 ml-auto"
            onClick={onMergeNext}
            disabled={busy}
            data-testid="merge-next-button"
            title="次のカットと結合（分割を取り消す等）"
          >
            次と結合
          </button>
        </div>
        <p className="text-[10px] mt-1.5" style={{color: "var(--text-muted)"}}>
          約 {(dur / splitParts).toFixed(1)}秒 × {splitParts}カットに分割します。元の素材割当はクリアされます。
        </p>
      </div>

      {/* テンプレート/エフェクト候補 */}
      <CutTemplatesPanel cut={cut} onUpdated={onUpdated}/>
    </div>
  );
};

const CutTemplatesPanel = ({ cut, onUpdated }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const adopted = cut.template_intents || [];

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const r = await getTemplateSuggestions(cut.id);
      setSuggestions(r.suggestions || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const onAdopt = async (sug) => {
    setBusy(true);
    try {
      await adoptTemplateIntent(cut.id, {
        type: sug.type,
        name: sug.name,
        description: sug.description,
        search_query: sug.search_query,
        search_url: sug.search_url,
        envato_url: sug.envato_url,
        duration: sug.duration,
        placement: sug.placement,
        source: "ai_suggested",
      });
      onUpdated && onUpdated();
    } finally { setBusy(false); }
  };

  const onUploadOwn = async (sug) => {
    const url = window.prompt(
      "自分でダウンロードしたテンプレートのファイルパスやURLを入力してください\n（後で Premiere の Essential Graphics で使うもの）",
      ""
    );
    if (!url) return;
    setBusy(true);
    try {
      await adoptTemplateIntent(cut.id, {
        type: sug?.type || "custom",
        name: sug ? `${sug.name} (アップロード版)` : "ユーザー指定テンプレ",
        description: sug?.description || "",
        asset_url: url,
        search_url: sug?.search_url,
        duration: sug?.duration,
        placement: sug?.placement,
        source: "user_uploaded",
      });
      onUpdated && onUpdated();
    } finally { setBusy(false); }
  };

  const onRemove = async (intent) => {
    if (!window.confirm(`「${intent.name}」を採用解除しますか？`)) return;
    setBusy(true);
    try {
      await deleteTemplateIntent(cut.id, intent.id);
      onUpdated && onUpdated();
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t pt-3" style={{borderColor: "var(--border-subtle)"}}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] uppercase tracking-wider flex items-center gap-1.5" style={{color: "var(--text-muted)"}}>
          <Sparkles size={10}/> テンプレート / エフェクト候補
        </label>
        {!suggestions.length && (
          <button className="btn-ghost !text-[10px] !py-1 !px-2" onClick={loadSuggestions} disabled={loading} data-testid="load-templates-button">
            {loading ? <Loader2 size={10} className="animate-spin"/> : <Wand2 size={10}/>}
            候補を取得
          </button>
        )}
      </div>

      {/* 採用済みリスト */}
      {adopted.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {adopted.map((t) => (
            <div key={t.id} className="panel p-2 flex items-center gap-2 text-[10px]" style={{borderColor: "rgba(168,192,154,0.4)", background: "rgba(168,192,154,0.05)"}} data-testid={`adopted-template-${t.id}`}>
              <CheckCircle2 size={11} style={{color: "var(--green-success)"}}/>
              <div className="flex-1 min-w-0">
                <div className="truncate" style={{color: "var(--text-primary)"}}>
                  {t.name} <span style={{color: "var(--text-muted)"}}>({t.type})</span>
                </div>
                {t.placement && <div className="text-[9px]" style={{color: "var(--text-muted)"}}>{t.placement}</div>}
                {t.source === "user_uploaded" && t.asset_url && (
                  <div className="text-[9px] truncate" style={{color: "var(--gold)"}}>📁 {t.asset_url}</div>
                )}
                {t.search_url && t.source === "ai_suggested" && (
                  <div className="flex gap-2">
                    {t.envato_url && (
                      <a href={t.envato_url} target="_blank" rel="noopener noreferrer" className="text-[9px] hover:underline" style={{color: "var(--gold)"}}>Envato で開く ↗</a>
                    )}
                    <a href={t.search_url} target="_blank" rel="noopener noreferrer" className="text-[9px] hover:underline" style={{color: "var(--text-muted)"}}>MA ↗</a>
                  </div>
                )}
              </div>
              <button onClick={() => onRemove(t)} disabled={busy} className="hover:opacity-70" data-testid={`remove-template-${t.id}`}>
                <Trash2 size={10} style={{color: "var(--red-warn)"}}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 候補リスト */}
      {suggestions.length > 0 && (
        <div className="space-y-1.5">
          {suggestions.map((s, i) => {
            const alreadyAdopted = adopted.some(a => a.name === s.name);
            return (
              <div key={i} className="panel p-2 text-[10px]" data-testid={`template-suggestion-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate" style={{color: "var(--text-primary)"}}>{s.name}</div>
                    <div className="text-[9px] mt-0.5" style={{color: "var(--text-muted)"}}>{s.description}</div>
                    {s.placement && (
                      <div className="text-[9px] mt-0.5" style={{color: "var(--text-secondary)"}}>📍 {s.placement}</div>
                    )}
                  </div>
                  <span className="chip text-[9px]" style={{color: "var(--gold)"}}>{s.type}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <button
                    className="btn-ghost !text-[9px] !py-1 !px-2"
                    onClick={() => onAdopt(s)}
                    disabled={busy || alreadyAdopted}
                    data-testid={`adopt-template-${i}`}
                  >
                    {alreadyAdopted ? "採用済" : "✓ 採用"}
                  </button>
                  <button
                    className="btn-ghost !text-[9px] !py-1 !px-2"
                    onClick={() => onUploadOwn(s)}
                    disabled={busy}
                    title="このアイデアを参考に、自分でEnvato/Motion Arrayから探してDLしたファイルパスを登録"
                    data-testid={`upload-own-template-${i}`}
                  >
                    📁 自分で探す
                  </button>
                  {s.envato_url && (
                    <a href={s.envato_url} target="_blank" rel="noopener noreferrer" className="btn-ghost !text-[9px] !py-1 !px-2 ml-auto" title="Envato Elementsで検索">
                      Envato ↗
                    </a>
                  )}
                  {s.search_url && (
                    <a href={s.search_url} target="_blank" rel="noopener noreferrer" className="btn-ghost !text-[9px] !py-1 !px-2" title="Motion Arrayで検索（参考）">
                      MA ↗
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          <button
            className="btn-ghost !text-[10px] w-full"
            onClick={() => onUploadOwn(null)}
            disabled={busy}
            data-testid="upload-custom-template"
          >
            + 候補にないテンプレを直接アップロード
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN EDITOR
// =============================================================================
const Editor = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [cuts, setCuts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [bgmTracks, setBgmTracks] = useState([]);
  const [selectedCutId, setSelectedCutId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [driveScanResult, setDriveScanResult] = useState(null); // { new_count, ...}
  const [driveImporting, setDriveImporting] = useState(false);
  const [driveTokenInvalid, setDriveTokenInvalid] = useState(false);
  const driveScanCheckedRef = React.useRef(false);

  const refresh = () => setRefreshKey(k => k + 1);

  const loadAll = async () => {
    try {
      const [p, ch, cu, ma, bg] = await Promise.all([
        getProject(projectId), listChapters(projectId), listCuts(projectId),
        listMaterials(projectId), listBgm(projectId),
      ]);
      setProject(p); setChapters(ch); setCuts(cu); setMaterials(ma); setBgmTracks(bg);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [projectId, refreshKey]);

  // Drive トークン状態を 30秒ごとにチェック
  useEffect(() => {
    let timer = null;
    const check = async () => {
      try {
        const s = await driveStatus();
        setDriveTokenInvalid(!!s.token_invalid);
      } catch (e) { /* ignore */ }
    };
    check();
    timer = setInterval(check, 30000);
    return () => clearInterval(timer);
  }, [refreshKey]);

  const handleReconnectDrive = async () => {
    try {
      const { authorization_url } = await driveConnect();
      const w = window.open(authorization_url, "drive_oauth", "width=600,height=700");
      // ポップアップ閉じたら再チェック
      const t = setInterval(async () => {
        if (w && w.closed) {
          clearInterval(t);
          const s = await driveStatus();
          setDriveTokenInvalid(!!s.token_invalid);
          if (!s.token_invalid) {
            alert("Drive 接続を更新しました ✅");
            refresh();
          }
        }
      }, 800);
    } catch (e) {
      alert("Drive 認証URL取得に失敗: " + (e.response?.data?.detail || e.message));
    }
  };

  // Drive 新着検知（プロジェクト読込後・1回だけ）
  useEffect(() => {
    if (!project || driveScanCheckedRef.current) return;
    if (!project.drive_root_folder_id) return;
    driveScanCheckedRef.current = true;
    (async () => {
      try {
        const r = await driveScanNew(projectId);
        if (r.new_count > 0) {
          setDriveScanResult(r);
        }
      } catch (e) {
        console.warn("Drive scan failed", e);
      }
    })();
  }, [project, projectId]);

  const handleImportNew = async (autoAnalyze) => {
    setDriveImporting(true);
    try {
      const r = await driveImportAllNew(projectId, autoAnalyze);
      alert(`✅ 取込完了\n  新規: ${r.imported?.length || 0} 件\n  選手紐付け: ${r.auto_linked || 0} 件\n${autoAnalyze ? "  AI解析はバックグラウンドで進行中..." : ""}`);
      setDriveScanResult(null);
      refresh();
    } catch (e) {
      alert("取込失敗: " + (e.response?.data?.detail || e.message));
    } finally {
      setDriveImporting(false);
    }
  };

  const selectedCut = cuts.find(c => c.id === selectedCutId);
  const totalDuration = project?.total_duration || 510;
  const totalUsed = cuts.reduce((s, c) => s + cutDuration(c), 0);
  const assignedCount = cuts.filter(c => c.assigned_material_id).length;

  if (!project) return (
    <div className="min-h-screen flex items-center justify-center text-xs" style={{color: "var(--text-muted)"}}>読み込み中...</div>
  );

  return (
    <div className="h-screen flex flex-col film-grain" data-testid="editor-page">
      {/* Drive トークン失効 警告バナー（管理者のみ目立つ赤色で表示） */}
      {driveTokenInvalid && (
        <div className="px-6 py-3 flex items-center justify-between gap-4"
             style={{background: "rgba(198,106,74,0.18)", borderBottom: "2px solid rgba(198,106,74,0.7)"}}
             data-testid="drive-token-invalid-banner">
          <div className="flex items-center gap-3 text-xs flex-1 min-w-0">
            <AlertTriangle size={16} style={{color: "#E07B5C"}}/>
            <div className="flex-1 min-w-0">
              <div className="font-semibold" style={{color: "#FFD3C2"}}>
                Drive 接続が失効しました（保護者の投稿が止まっています）
              </div>
              <div className="text-[11px] mt-0.5" style={{color: "rgba(255,211,194,0.85)"}}>
                Google のセキュリティ仕様で約7日経過するとリフレッシュトークンが失効します。下のボタンで再認証してください。
              </div>
            </div>
          </div>
          <button
            className="btn-gold !text-xs"
            onClick={handleReconnectDrive}
            data-testid="reconnect-drive-btn"
          >
            <Cloud size={12}/> Drive 再接続
          </button>
        </div>
      )}

      {/* Drive 新着検知バナー */}
      {driveScanResult && driveScanResult.new_count > 0 && (
        <div className="px-6 py-3 flex items-center justify-between gap-4" style={{background: "rgba(212,165,116,0.12)", borderBottom: "1px solid rgba(212,165,116,0.4)"}} data-testid="drive-new-banner">
          <div className="flex items-center gap-3 text-xs flex-1 min-w-0">
            <Cloud size={16} style={{color: "var(--gold)"}}/>
            <div>
              <div style={{color: "var(--gold)"}} className="font-medium">
                Drive に新着 <b>{driveScanResult.new_count}</b> 件あります
              </div>
              <div className="text-[10px]" style={{color: "var(--text-muted)"}}>
                保護者からの投稿が届いています。取り込んで AI 解析しますか？（バックグラウンドで進行）
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              className="btn-ghost !text-[11px]"
              onClick={() => setDriveScanResult(null)}
              disabled={driveImporting}
              data-testid="drive-new-dismiss"
            >
              後で
            </button>
            <button
              className="btn-ghost !text-[11px]"
              onClick={() => handleImportNew(false)}
              disabled={driveImporting}
              data-testid="drive-import-no-ai"
            >
              取込のみ
            </button>
            <button
              className="btn-gold !text-[11px]"
              onClick={() => handleImportNew(true)}
              disabled={driveImporting}
              data-testid="drive-import-with-ai"
            >
              {driveImporting ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
              取込 + AI解析
            </button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-3 border-b" style={{borderColor: "var(--border-subtle)", background: "var(--bg-panel)"}}>
        <div className="flex items-center gap-4">
          <Link to="/projects" className="flex items-center gap-2 text-xs hover:opacity-80" style={{color: "var(--text-secondary)"}} data-testid="back-to-projects">
            <ChevronLeft size={14}/> プロジェクト一覧
          </Link>
          <div className="divider-vertical"/>
          <div>
            <span className="text-[10px] uppercase tracking-widest" style={{color: "var(--text-muted)"}}>{project.type === "practice" ? "練習" : "本番"}</span>
            <h1 className="font-display text-base" data-testid="project-title">{project.name}</h1>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right text-xs">
            <div className="text-[10px]" style={{color: "var(--text-muted)"}}>尺</div>
            <div className={`text-tc font-medium ${totalUsed > totalDuration + 1 ? "text-[var(--red-warn)]" : "text-[var(--gold)]"}`} data-testid="total-duration-display">
              {formatTC(totalUsed)} / {formatTC(totalDuration)}
            </div>
          </div>
          <div className="text-right text-xs">
            <div className="text-[10px]" style={{color: "var(--text-muted)"}}>割当</div>
            <div className="text-tc font-medium" style={{color: "var(--green-success)"}} data-testid="assigned-display">
              {assignedCount} / {cuts.length}
            </div>
          </div>
          <div className="divider-vertical"/>
          <button className="btn-ghost" onClick={() => setDialog("drive")} title="Google Drive" data-testid="open-drive"><Cloud size={12}/></button>
          <button className="btn-ghost" onClick={() => setDialog("players")} title="選手管理" data-testid="open-players"><Users size={12}/></button>
          <button className="btn-ghost" onClick={() => setDialog("intros")} title="選手紹介ビルダー" data-testid="open-intros"><Film size={12}/></button>
          <button className="btn-ghost" onClick={() => setDialog("analysis")} title="AI解析" data-testid="open-analysis"><ScanFace size={12}/></button>
          <button className="btn-ghost" onClick={() => setDialog("bgm")} title="BGM" data-testid="open-bgm"><Music size={12}/></button>
          <button className="btn-ghost" onClick={() => setDialog("advisor")} title="AI構成アドバイザー" data-testid="open-advisor"><Lightbulb size={12}/></button>
          <button className="btn-ghost" onClick={() => setDialog("versions")} title="バージョン" data-testid="open-versions"><History size={12}/></button>
          <button className="btn-gold" onClick={() => setDialog("export")} data-testid="open-export">
            <Download size={12}/> 書き出し
          </button>
        </div>
      </header>

      {/* 3-pane content */}
      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        <aside className="col-span-3 border-r overflow-hidden" style={{borderColor: "var(--border-subtle)", background: "var(--bg-panel)"}}>
          <MaterialLibrary projectId={projectId} selectedCut={selectedCut} onPickMaterial={(m) => {
            // 素材クリック時：選択中のカットに割当
            if (selectedCutId) {
              assignMaterialToCut(selectedCutId, m.id, 0).then(refresh);
            }
          }} refreshKey={refreshKey} onRefresh={refresh}/>
        </aside>

        <main className="col-span-6 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto" style={{background: "var(--bg-base)"}}>
            <CenterPanel project={project} selectedCut={selectedCut} materials={materials} onAssigned={refresh} refreshKey={refreshKey}/>
          </div>
          {selectedCut && <CutInspector cut={selectedCut} onUpdated={refresh}/>}
          <TimelineBar chapters={chapters} cuts={cuts} materials={materials} totalDuration={totalDuration} selectedCutId={selectedCutId} onSelectCut={(c) => setSelectedCutId(c.id)} bgmTracks={bgmTracks}/>
        </main>

        <aside className="col-span-3 border-l overflow-hidden" style={{borderColor: "var(--border-subtle)", background: "var(--bg-panel)"}}>
          <StoryboardPanel
            chapters={chapters}
            cuts={cuts}
            materials={materials}
            selectedCutId={selectedCutId}
            onSelectCut={(c) => setSelectedCutId(c.id)}
            onCutsChanged={refresh}
          />
        </aside>
      </div>

      {/* Dialogs */}
      {dialog === "export" && <ExportDialog project={project} onClose={() => setDialog(null)}/>}
      {dialog === "drive" && <DriveDialog projectId={projectId} project={project} onClose={() => setDialog(null)} onChanged={refresh}/>}
      {dialog === "versions" && <VersionsDialog projectId={projectId} onClose={() => setDialog(null)} onRestored={refresh}/>}
      {dialog === "players" && <PlayersDialog projectId={projectId} onClose={() => setDialog(null)}/>}
      {dialog === "intros" && <PlayerIntroBuilderDialog projectId={projectId} onClose={() => setDialog(null)} onBuilt={refresh}/>}
      {dialog === "analysis" && <AnalysisDialog projectId={projectId} onClose={() => setDialog(null)}/>}
      {dialog === "bgm" && <BgmDialog projectId={projectId} totalDuration={totalDuration} onClose={() => setDialog(null)} onChanged={refresh}/>}
      {dialog === "advisor" && <AdvisorDialog projectId={projectId} onClose={() => setDialog(null)}/>}
    </div>
  );
};

export default Editor;
